import React, { AnchorHTMLAttributes, ButtonHTMLAttributes, useEffect, useRef, useState } from 'react'
import mergeRefs from 'react-merge-refs'
import cn from 'classnames'

import { Spinner } from '../Spinner'

import styles from './index.module.css'
import type { CommonButtonProps } from './typing'

/* 
      Минимальное отображение лоадера - 500мс
      чтобы при быстрых ответах от сервера кнопка не мерцала
  */
const LOADER_MIN_DISPLAY_INTERVAL = 500

export const Button = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, CommonButtonProps>(
  (
    {
      view = 'primary',
      leftAddons,
      rightAddons,
      size = 'md',
      block = false,
      className,
      href,
      loading = false,
      nowrap = false,
      Component = href ? 'a' : 'button',
      onClick,
      children,
      ...restProps
    },
    ref,
  ) => {
    const buttonRef = useRef<HTMLElement>(null)

    const [loaderTimePassed, setLoaderTimePassed] = useState(true)
    const timerId = useRef(0)

    const showLoader = loading || !loaderTimePassed

    const classes = {
      className: cn(
        styles.component,
        styles[view],
        styles[size],
        {
          [styles.block]: block,
          [styles.withLeftAddons]: Boolean(leftAddons),
          [styles.withRightAddons]: Boolean(rightAddons),
          [styles.loading]: loading,
        },
        className,
      ),
    }

    const { disabled, type = 'button', ...restButtonProps } = restProps as ButtonHTMLAttributes<HTMLButtonElement>

    const buttonChildren = (
      <>
        {leftAddons && <span className={styles.addons}>{leftAddons}</span>}
        {children && (
          <span
            className={cn(styles.children, {
              [styles.nowrap]: nowrap,
            })}
          >
            {children}
          </span>
        )}

        {showLoader && <Spinner color="#ffffff" size={16} className={styles.spinner} />}

        {rightAddons && <span className={styles.addons}>{rightAddons}</span>}
      </>
    )

    useEffect(() => {
      if (loading) {
        setLoaderTimePassed(false)

        timerId.current = window.setTimeout(() => {
          setLoaderTimePassed(true)
        }, LOADER_MIN_DISPLAY_INTERVAL)
      }
    }, [loading])

    useEffect(
      () => () => {
        window.clearTimeout(timerId.current)
      },
      [],
    )

    const handleClick = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent> & React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      if (disabled || showLoader) {
        e.preventDefault()
        e.stopPropagation()

        return
      }
      onClick?.(e)
    }

    if (href) {
      const { target } = restProps as AnchorHTMLAttributes<HTMLAnchorElement>

      return (
        <Component
          rel={target === '_blank' ? 'noreferrer noopener' : undefined}
          {...classes}
          {...(restProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
          onClick={handleClick}
          disabled={disabled || showLoader}
          ref={mergeRefs([buttonRef, ref])}
        >
          {buttonChildren}
        </Component>
      )
    }

    return (
      <Component
        {...classes}
        {...restButtonProps}
        onClick={handleClick}
        type={type}
        disabled={disabled || showLoader}
        ref={mergeRefs([buttonRef, ref])}
      >
        {buttonChildren}
      </Component>
    )
  },
)

Button.displayName = 'Button'
